/**
 * Olive.III Native Product Customization System
 * Manages ReadyMade vs Customized purchase modes, measurement validation,
 * variant synchronization, and native line-item properties.
 */

(function () {
  'use strict';

  class ProductCustomizationController {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.section;
      this.productFormId = container.dataset.productFormId;

      this.mode = null; // Neither option selected by default

      this.initElements();
      if (!this.form) return;

      this.initVariants();
      this.bindEvents();
      this.syncPropertiesToForm();
    }

    initElements() {
      this.btnReadyMade = this.container.querySelector('[data-mode="readymade"]');
      this.btnCustomized = this.container.querySelector('[data-mode="customized"]');
      this.modeToggle = this.container.querySelector('.luxury-mode-toggle');
      this.orderTypeInput = this.container.querySelector('input[name="properties[Order Type]"]');
      this.customPanel = this.container.querySelector('.luxury-custom-panel');
      this.popup = this.container.querySelector('.luxury-mode-popup-overlay')
        || document.getElementById(`ModeSelectionPopup-${this.sectionId}`)
        || document.querySelector('.luxury-mode-popup-overlay');

      this.requiredInputs = Array.from(
        this.container.querySelectorAll('.luxury-custom-input[data-required="true"]')
      );
      this.notesTextarea = this.container.querySelector('.luxury-custom-textarea');

      const scope = this.container.closest('quick-add-modal')
        || this.container.closest('.product__info-container')
        || this.container.closest('.product__info-wrapper')
        || this.container.closest('.product')
        || document;

      // Main product form & variant elements
      this.form = (this.productFormId && document.getElementById(this.productFormId))
        || scope.querySelector(`form[data-type="add-to-cart-form"]`)
        || scope.querySelector(`form[action*="/cart/add"]`)
        || (this.productFormId && scope.querySelector(`#${this.productFormId}`))
        || document.querySelector(`form[data-type="add-to-cart-form"]`);

      this.variantIdInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      if (this.variantIdInput) {
        this.variantIdInput.disabled = false;
      }
      this.productFormElement = this.form ? this.form.closest('product-form') : null;

      // Variant picker elements
      this.variantRadios = scope.querySelector(`variant-radios[data-section="${this.sectionId}"]`)
        || this.container.closest('variant-radios')
        || scope.querySelector('variant-radios')
        || document.getElementById(`variant-radios-${this.sectionId}`);

      this.sizeFieldset = this.findSizeFieldset();
      this.colorFieldset = this.findColorFieldset();

      this.readyMadeLengthWrapper = scope.querySelector(`#ReadyMadeLength-${this.sectionId}`)
        || scope.querySelector(`#ReadyMadeLength-selects-${this.sectionId}`)
        || scope.querySelector('.luxury-readymade-length-wrapper')
        || document.getElementById(`ReadyMadeLength-${this.sectionId}`)
        || document.getElementById(`ReadyMadeLength-selects-${this.sectionId}`);
      this.readyMadeLengthSelect = this.readyMadeLengthWrapper
        ? this.readyMadeLengthWrapper.querySelector('select')
        : null;

      this.readyMadeNotesWrapper = scope.querySelector(`#ReadyMadeNotes-${this.sectionId}`)
        || scope.querySelector(`#ReadyMadeNotes-selects-${this.sectionId}`)
        || scope.querySelector('.luxury-readymade-notes-wrapper')
        || document.getElementById(`ReadyMadeNotes-${this.sectionId}`)
        || document.getElementById(`ReadyMadeNotes-selects-${this.sectionId}`);
      this.readyMadeNotesTextarea = this.readyMadeNotesWrapper
        ? this.readyMadeNotesWrapper.querySelector('textarea')
        : null;

      if (this.readyMadeLengthWrapper && this.sizeFieldset) {
        this.sizeFieldset.after(this.readyMadeLengthWrapper);
      }
      if (this.readyMadeNotesWrapper && this.readyMadeLengthWrapper) {
        this.readyMadeLengthWrapper.after(this.readyMadeNotesWrapper);
      } else if (this.readyMadeNotesWrapper && this.sizeFieldset && this.readyMadeNotesWrapper.previousElementSibling !== this.sizeFieldset) {
        this.sizeFieldset.after(this.readyMadeNotesWrapper);
      }

      // Ensure sizes selector is hidden until ReadyMade is explicitly clicked
      if (this.mode !== 'readymade') {
        this.hideSizeFieldsets();
        if (this.readyMadeLengthWrapper) {
          this.readyMadeLengthWrapper.hidden = true;
          this.readyMadeLengthWrapper.setAttribute('hidden', '');
          this.readyMadeLengthWrapper.style.setProperty('display', 'none', 'important');
        }
        if (this.readyMadeLengthSelect) {
          this.readyMadeLengthSelect.disabled = true;
        }
        if (this.readyMadeNotesWrapper) {
          this.readyMadeNotesWrapper.hidden = true;
          this.readyMadeNotesWrapper.setAttribute('hidden', '');
          this.readyMadeNotesWrapper.style.setProperty('display', 'none', 'important');
        }
        if (this.readyMadeNotesTextarea) {
          this.readyMadeNotesTextarea.disabled = true;
        }
      }
    }

    findSizeFieldsets() {
      const parent = this.variantRadios || this.container.closest('.product__info-container') || document;
      let found = Array.from(parent.querySelectorAll(
        'fieldset[data-option-type="size"], fieldset.option-name-size, fieldset.option-name-sizes, fieldset[class*="option-name-size"], fieldset:has(.luxury-swatch-header--size)'
      ));
      if (found.length === 0) {
        found = Array.from(document.querySelectorAll(
          `#variant-radios-${this.sectionId} fieldset[data-option-type="size"], #variant-radios-${this.sectionId} fieldset.option-name-size, #variant-radios-${this.sectionId} fieldset.option-name-sizes, #variant-radios-${this.sectionId} fieldset:has(.luxury-swatch-header--size)`
        ));
      }
      if (found.length === 0) {
        const infoWrap = this.container.closest('.product__info-container') || document;
        const dropdownInputs = infoWrap.querySelectorAll('.product-form__input--dropdown');
        dropdownInputs.forEach((dd) => {
          const text = (dd.textContent || '').toLowerCase();
          if (text.includes('size') || text.includes('مقاس') || text.includes('حجم') || text.includes('length') || text.includes('طول')) {
            found.push(dd);
          }
        });
      }
      return found;
    }

    findSizeFieldset() {
      const list = this.findSizeFieldsets();
      return list.length > 0 ? list[0] : null;
    }

    findColorFieldset() {
      const parent = this.variantRadios || this.container.closest('.product__info-container') || document;
      return parent.querySelector(
        'fieldset[data-option-type="color"], fieldset.option-name-color, fieldset.option-name-colour, fieldset[class*="option-name-color"], fieldset:has(.luxury-swatch-header--color)'
      ) || document.querySelector(
        `#variant-radios-${this.sectionId} fieldset[data-option-type="color"], #variant-radios-${this.sectionId} fieldset.option-name-color, #variant-radios-${this.sectionId} fieldset.option-name-colour`
      );
    }

    hideSizeFieldsets() {
      const sizeFieldsets = this.findSizeFieldsets();
      sizeFieldsets.forEach((fs) => {
        fs.style.setProperty('display', 'none', 'important');
        fs.setAttribute('hidden', '');
        fs.setAttribute('aria-hidden', 'true');
        fs.classList.add('is-hidden');
      });
      if (this.variantRadios) {
        this.variantRadios.classList.add('has-customized-mode');
        this.variantRadios.classList.remove('has-readymade-mode');
      }
      const infoContainer = this.container.closest('.product__info-container');
      if (infoContainer) {
        infoContainer.classList.add('has-customized-mode');
        infoContainer.classList.remove('has-readymade-mode');
      }
      this.container.classList.remove('has-readymade-mode');
    }

    showSizeFieldsets() {
      const sizeFieldsets = this.findSizeFieldsets();
      sizeFieldsets.forEach((fs) => {
        fs.style.removeProperty('display');
        fs.removeAttribute('hidden');
        fs.removeAttribute('aria-hidden');
        fs.classList.remove('is-hidden');
      });
      if (this.variantRadios) {
        this.variantRadios.classList.remove('has-customized-mode');
        this.variantRadios.classList.add('has-readymade-mode');
      }
      const infoContainer = this.container.closest('.product__info-container');
      if (infoContainer) {
        infoContainer.classList.remove('has-customized-mode');
        infoContainer.classList.add('has-readymade-mode');
      }
      this.container.classList.add('has-readymade-mode');
    }

    initVariants() {
      const scope = this.container.closest('quick-add-modal') || this.container.closest('.product') || document;
      const scriptTag = scope.querySelector(`#ProductCustomizationVariants-${this.sectionId}`) || document.getElementById(`ProductCustomizationVariants-${this.sectionId}`);
      if (scriptTag) {
        try {
          this.variants = JSON.parse(scriptTag.textContent);
        } catch (e) {
          console.warn('Could not parse variant data for customization:', e);
          this.variants = [];
        }
      } else {
        this.variants = [];
      }
    }

    bindEvents() {
      // Mode toggle buttons
      if (this.btnReadyMade) {
        this.btnReadyMade.addEventListener('click', () => this.setMode('readymade'));
      }
      if (this.btnCustomized) {
        this.btnCustomized.addEventListener('click', () => this.setMode('customized'));
      }

      // Isolate internal change events from bubbling to variant-radios
      this.container.addEventListener('change', (e) => {
        if (e.target && this.container.contains(e.target)) {
          e.stopPropagation();
        }
      });

      // Real-time validation clearance and property synchronization on typing
      this.requiredInputs.forEach((input) => {
        input.addEventListener('input', () => {
          if (input.value && parseFloat(input.value) > 0) {
            this.clearInputError(input);
          }
          this.syncPropertiesToForm();
        });
      });

      if (this.notesTextarea) {
        this.notesTextarea.addEventListener('input', () => {
          if (this.readyMadeNotesTextarea && this.readyMadeNotesTextarea !== this.notesTextarea) {
            this.readyMadeNotesTextarea.value = this.notesTextarea.value;
          }
          this.syncPropertiesToForm();
        });
      }

      if (this.readyMadeLengthSelect) {
        this.readyMadeLengthSelect.addEventListener('change', () => {
          this.readyMadeLengthSelect.classList.remove('is-invalid');
          const errEl = document.getElementById(`Error-ReadyMadeLength-${this.sectionId}`)
            || document.getElementById(`Error-ReadyMadeLength-selects-${this.sectionId}`);
          if (errEl) {
            errEl.hidden = true;
            errEl.setAttribute('hidden', '');
          }
          this.syncPropertiesToForm();
        });
      }

      if (this.readyMadeNotesTextarea) {
        this.readyMadeNotesTextarea.addEventListener('input', () => {
          if (this.notesTextarea && this.notesTextarea !== this.readyMadeNotesTextarea) {
            this.notesTextarea.value = this.readyMadeNotesTextarea.value;
          }
          this.syncPropertiesToForm();
        });
      }

      // Intercept submit on the product form
      if (this.form) {
        this.form.addEventListener('submit', (e) => this.handleFormSubmit(e), true);
      }

      // Intercept dynamic checkout / Buy It Now clicks
      document.addEventListener('click', (e) => this.handleDynamicCheckoutClick(e), true);

      // Listen to color changes to keep variant ID synchronized in Customized mode
      if (this.variantRadios) {
        this.variantRadios.addEventListener('change', () => this.onVariantOrColorChange());
      }

      // Mode Selection Error Popup event listeners
      if (this.popup) {
        this.popup.addEventListener('click', (e) => {
          if (e.target === this.popup || e.target.closest('.luxury-mode-popup-close')) {
            this.hideModeErrorPopup();
          }
          const chooseBtn = e.target.closest('[data-choose-mode]');
          if (chooseBtn) {
            const chosenMode = chooseBtn.getAttribute('data-choose-mode');
            this.setMode(chosenMode);
          }
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.popup && !this.popup.hidden) {
            this.hideModeErrorPopup();
          }
        });
      }
    }

    setMode(newMode) {
      if (!newMode) return;
      if (this.mode === newMode) return;
      this.mode = newMode;
      this.hideModeErrorPopup();

      if (this.modeToggle) {
        this.modeToggle.classList.add('has-selection');
      }

      const sizeFieldsets = this.findSizeFieldsets();
      const infoContainer = this.container.closest('.product__info-container');

      if (this.mode === 'readymade') {
        // 1. Update Mode Buttons
        this.btnReadyMade.classList.add('is-active');
        this.btnReadyMade.setAttribute('aria-checked', 'true');
        this.btnCustomized.classList.remove('is-active');
        this.btnCustomized.setAttribute('aria-checked', 'false');

        // 2. Order Type Property
        if (this.orderTypeInput) {
          this.orderTypeInput.value = 'ReadyMade';
        }

        // 3. Hide Custom Panel
        if (this.customPanel) {
          this.customPanel.hidden = true;
          this.customPanel.setAttribute('aria-hidden', 'true');
        }

        // 4. Reveal ReadyMade Size Selector, Length Dropdown & Notes Field
        this.showSizeFieldsets();
        if (this.readyMadeLengthWrapper) {
          this.readyMadeLengthWrapper.hidden = false;
          this.readyMadeLengthWrapper.removeAttribute('hidden');
          this.readyMadeLengthWrapper.removeAttribute('aria-hidden');
          this.readyMadeLengthWrapper.style.removeProperty('display');
        }
        if (this.readyMadeLengthSelect) {
          this.readyMadeLengthSelect.disabled = false;
        }
        if (this.readyMadeNotesWrapper) {
          this.readyMadeNotesWrapper.hidden = false;
          this.readyMadeNotesWrapper.removeAttribute('hidden');
          this.readyMadeNotesWrapper.removeAttribute('aria-hidden');
          this.readyMadeNotesWrapper.style.removeProperty('display');
        }
        if (this.readyMadeNotesTextarea) {
          this.readyMadeNotesTextarea.disabled = false;
        }

        // 5. Disable customized inputs so FormData omits them
        this.requiredInputs.forEach((input) => {
          input.disabled = true;
          this.clearInputError(input);
        });
        if (this.notesTextarea) {
          this.notesTextarea.disabled = true;
        }

        // 6. Restore variant selection based on currently selected radio options
        if (this.variantRadios && typeof this.variantRadios.updateMasterId === 'function') {
          this.variantRadios.updateOptions();
          this.variantRadios.updateMasterId();
          this.variantRadios.updateVariantInput();
        }
      } else if (this.mode === 'customized') {
        // 1. Update Mode Buttons
        this.btnCustomized.classList.add('is-active');
        this.btnCustomized.setAttribute('aria-checked', 'true');
        this.btnReadyMade.classList.remove('is-active');
        this.btnReadyMade.setAttribute('aria-checked', 'false');

        // 2. Order Type Property
        if (this.orderTypeInput) {
          this.orderTypeInput.value = 'Customized';
        }

        // 3. Show Custom Panel
        if (this.customPanel) {
          this.customPanel.hidden = false;
          this.customPanel.setAttribute('aria-hidden', 'false');
        }

        // 4. Hide ReadyMade Size Selector, Length Dropdown & ReadyMade Notes Field
        this.hideSizeFieldsets();
        if (this.readyMadeLengthWrapper) {
          this.readyMadeLengthWrapper.hidden = true;
          this.readyMadeLengthWrapper.setAttribute('hidden', '');
          this.readyMadeLengthWrapper.setAttribute('aria-hidden', 'true');
          this.readyMadeLengthWrapper.style.setProperty('display', 'none', 'important');
        }
        if (this.readyMadeLengthSelect) {
          this.readyMadeLengthSelect.disabled = true;
        }
        if (this.readyMadeNotesWrapper) {
          this.readyMadeNotesWrapper.hidden = true;
          this.readyMadeNotesWrapper.setAttribute('hidden', '');
          this.readyMadeNotesWrapper.setAttribute('aria-hidden', 'true');
          this.readyMadeNotesWrapper.style.setProperty('display', 'none', 'important');
        }
        if (this.readyMadeNotesTextarea) {
          this.readyMadeNotesTextarea.disabled = true;
        }

        // 5. Enable all customization inputs
        this.requiredInputs.forEach((input) => {
          input.disabled = false;
        });
        if (this.notesTextarea) {
          this.notesTextarea.disabled = false;
        }

        // 6. Synchronize variant for current colour
        this.syncCustomVariant();
      }

      // 7. Update mirrored line item properties in form
      this.syncPropertiesToForm();
    }

    onVariantOrColorChange() {
      if (this.mode !== 'readymade') {
        this.hideSizeFieldsets();
      }
      if (this.mode === 'customized') {
        this.syncCustomVariant();
      }
    }

    /**
     * Resolves the correct variant for Customized mode.
     * Checks if a custom-capable variant exists for the selected colour.
     * If not, uses the selected colour's base variant ID.
     */
    syncCustomVariant() {
      if (!this.variants || this.variants.length === 0 || !this.variantIdInput) return;

      const selectedColor = this.getSelectedColor();

      // Check if a dedicated 'CUSTOM' variant exists for this color
      let matchedVariant = null;
      if (selectedColor) {
        // First look for custom variant matching this color
        matchedVariant = this.variants.find((v) => {
          const hasColor = v.options.some(
            (opt) => opt && opt.toLowerCase() === selectedColor.toLowerCase()
          );
          const isCustom =
            (v.title && v.title.toLowerCase().includes('custom')) ||
            v.options.some((opt) => opt && opt.toLowerCase().includes('custom'));
          return hasColor && isCustom;
        });

        // If no dedicated custom variant, pick the first available variant for this color
        if (!matchedVariant) {
          matchedVariant = this.variants.find((v) => {
            return v.options.some(
              (opt) => opt && opt.toLowerCase() === selectedColor.toLowerCase()
            );
          });
        }
      }

      // If still not matched, fall back to current form variant
      if (matchedVariant) {
        this.variantIdInput.value = matchedVariant.id;
        this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    getSelectedColor() {
      if (!this.colorFieldset) return null;
      const checkedInput = this.colorFieldset.querySelector('input:checked');
      return checkedInput ? checkedInput.value : null;
    }

    validateMeasurements() {
      if (this.mode !== 'customized') return true;

      let isValid = true;
      let firstInvalidInput = null;

      this.requiredInputs.forEach((input) => {
        const val = input.value.trim();
        const numVal = parseFloat(val);

        if (!val || isNaN(numVal) || numVal <= 0) {
          isValid = false;
          this.setInputError(input);
          if (!firstInvalidInput) {
            firstInvalidInput = input;
          }
        } else {
          this.clearInputError(input);
        }
      });

      if (!isValid && firstInvalidInput) {
        firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstInvalidInput.focus();
      }

      return isValid;
    }

    setInputError(input) {
      input.classList.add('is-invalid');
      const field = input.closest('.luxury-custom-field');
      if (field) {
        const errorEl = field.querySelector('.luxury-custom-field__error');
        if (errorEl) errorEl.hidden = false;
      }
    }

    clearInputError(input) {
      input.classList.remove('is-invalid');
      const field = input.closest('.luxury-custom-field');
      if (field) {
        const errorEl = field.querySelector('.luxury-custom-field__error');
        if (errorEl) errorEl.hidden = true;
      }
    }

    /**
     * Ensures all enabled line-item properties exist directly inside the product form.
     * Guarantees 100% cross-browser serialization in new FormData(this.form) (Safari/WebKit/Chrome/Firefox).
     */
    syncPropertiesToForm() {
      if (!this.form) return;

      // Remove existing mirrored inputs inside form
      this.form.querySelectorAll('input[data-customization-mirror="true"]').forEach((el) => el.remove());

      if (!this.mode) return;

      // If mode is ReadyMade, ensure Order Type: ReadyMade, Length, and optional notes are present
      if (this.mode === 'readymade') {
        const hiddenOrderType = document.createElement('input');
        hiddenOrderType.type = 'hidden';
        hiddenOrderType.name = 'properties[Order Type]';
        hiddenOrderType.value = 'ReadyMade';
        hiddenOrderType.setAttribute('data-customization-mirror', 'true');
        this.form.appendChild(hiddenOrderType);

        const readymadeLength = (this.readyMadeLengthSelect && this.readyMadeLengthSelect.value.trim()) || '';
        if (readymadeLength) {
          const hiddenLength = document.createElement('input');
          hiddenLength.type = 'hidden';
          hiddenLength.name = 'properties[Length]';
          hiddenLength.value = readymadeLength;
          hiddenLength.setAttribute('data-customization-mirror', 'true');
          this.form.appendChild(hiddenLength);
        }

        const readymadeNotes = (this.readyMadeNotesTextarea && this.readyMadeNotesTextarea.value.trim())
          || (this.notesTextarea && this.notesTextarea.value.trim()) || '';
        if (readymadeNotes) {
          const hiddenNotes = document.createElement('input');
          hiddenNotes.type = 'hidden';
          hiddenNotes.name = 'properties[Notes & Suggestions]';
          hiddenNotes.value = readymadeNotes;
          hiddenNotes.setAttribute('data-customization-mirror', 'true');
          this.form.appendChild(hiddenNotes);
        }
        return;
      }

      // If mode is Customized, mirror Order Type, all valid measurement inputs, and optional notes
      const hiddenOrderType = document.createElement('input');
      hiddenOrderType.type = 'hidden';
      hiddenOrderType.name = 'properties[Order Type]';
      hiddenOrderType.value = 'Customized';
      hiddenOrderType.setAttribute('data-customization-mirror', 'true');
      this.form.appendChild(hiddenOrderType);

      this.requiredInputs.forEach((input) => {
        const val = input.value ? input.value.trim() : '';
        if (val) {
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = input.name;
          hidden.value = val;
          hidden.setAttribute('data-customization-mirror', 'true');
          this.form.appendChild(hidden);
        }
      });

      const customNotes = (this.notesTextarea && this.notesTextarea.value.trim())
        || (this.readyMadeNotesTextarea && this.readyMadeNotesTextarea.value.trim()) || '';
      if (customNotes) {
        const hiddenNotes = document.createElement('input');
        hiddenNotes.type = 'hidden';
        hiddenNotes.name = 'properties[Notes & Suggestions]';
        hiddenNotes.value = customNotes;
        hiddenNotes.setAttribute('data-customization-mirror', 'true');
        this.form.appendChild(hiddenNotes);
      }
    }

    handleFormSubmit(e) {
      if (!this.mode) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.resetSubmitButtonState();
        this.showModeErrorPopup();
        return false;
      }

      if (this.mode === 'readymade') {
        if (this.readyMadeLengthSelect && !this.readyMadeLengthSelect.disabled && !this.readyMadeLengthSelect.value) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.resetSubmitButtonState();
          this.readyMadeLengthSelect.classList.add('is-invalid');
          const errEl = document.getElementById(`Error-ReadyMadeLength-${this.sectionId}`)
            || document.getElementById(`Error-ReadyMadeLength-selects-${this.sectionId}`);
          if (errEl) {
            errEl.hidden = false;
            errEl.removeAttribute('hidden');
          }
          this.readyMadeLengthSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
          this.readyMadeLengthSelect.focus();
          return false;
        }
      }

      if (this.mode === 'customized') {
        const isValid = this.validateMeasurements();
        if (!isValid) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.resetSubmitButtonState();
          return false;
        }
      }

      // Sync verified properties directly into the form before FormData is generated
      this.syncPropertiesToForm();
    }

    handleDynamicCheckoutClick(e) {
      // Check if click was on or inside the dynamic checkout button
      const target = e.target;
      const paymentButton = target.closest(
        '[data-buy-it-now], .luxury-buy-it-now-btn, .shopify-payment-button__button, .shopify-payment-button, [data-testid="Checkout-button"], .shopify-payment-button__button--unbranded, [data-action="buy-now"]'
      );
      if (!paymentButton) return;

      // Verify button does not belong to a completely different product section
      const buttonSection = paymentButton.closest('.product, .main-product-template, [id^="MainProduct-"], .product__info-container, quick-add-modal');
      const controllerSection = this.container.closest('.product, .main-product-template, [id^="MainProduct-"], .product__info-container, quick-add-modal');
      if (buttonSection && controllerSection && buttonSection !== controllerSection) {
        return;
      }

      e.__buyNowHandled = true;

      // MANDATORY CHECK: If neither ReadyMade nor Customized is selected, show popup and halt!
      if (!this.mode) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.resetSubmitButtonState();
        this.showModeErrorPopup();
        return false;
      }

      if (this.mode === 'readymade') {
        if (this.readyMadeLengthSelect && !this.readyMadeLengthSelect.disabled && !this.readyMadeLengthSelect.value) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.resetSubmitButtonState();
          this.readyMadeLengthSelect.classList.add('is-invalid');
          const errEl = document.getElementById(`Error-ReadyMadeLength-${this.sectionId}`)
            || document.getElementById(`Error-ReadyMadeLength-selects-${this.sectionId}`);
          if (errEl) {
            errEl.hidden = false;
            errEl.removeAttribute('hidden');
          }
          this.readyMadeLengthSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
          this.readyMadeLengthSelect.focus();
          return false;
        }
      }

      if (this.mode === 'customized') {
        const isValid = this.validateMeasurements();
        if (!isValid) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.resetSubmitButtonState();
          return false;
        }
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (this.variantIdInput) {
        this.variantIdInput.disabled = false;
      }

      // Sync verified properties directly into the form
      this.syncPropertiesToForm();

      if (!this.form) {
        window.location.href = (window.routes && window.routes.checkout_url) || '/checkout';
        return false;
      }

      const clickBtn = paymentButton.tagName === 'BUTTON' ? paymentButton : paymentButton.querySelector('button') || paymentButton;
      if (clickBtn) {
        clickBtn.classList.add('loading');
        clickBtn.setAttribute('aria-disabled', 'true');
        const spinner = clickBtn.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.remove('hidden');
      }

      const checkoutUrl = (window.routes && window.routes.checkout_url) || '/checkout';
      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

      const formData = new FormData(this.form);

      fetch(addUrl, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript, application/json'
        },
        body: formData
      })
      .then((res) => {
        if (!res.ok) {
          return res.json().then((errData) => Promise.reject(errData));
        }
        return res.json();
      })
      .then(() => {
        window.location.href = checkoutUrl;
      })
      .catch((err) => {
        console.warn('Buy It Now redirecting to checkout:', err);
        window.location.href = checkoutUrl;
      });

      return false;
    }

    showModeErrorPopup() {
      if (!this.popup) {
        this.popup = document.getElementById(`ModeSelectionPopup-${this.sectionId}`)
          || document.querySelector('.luxury-mode-popup-overlay');
      }

      if (!this.popup) {
        alert("Would you like Ready-made or Customized?");
        return;
      }

      if (!this.popup.hidden && this.popup.classList.contains('is-visible')) {
        return; // Prevent duplicate popup executions
      }

      // Ensure popup is appended to document.body so it is top-level and never constrained by parent transforms
      if (this.popup.parentElement !== document.body) {
        document.body.appendChild(this.popup);
      }

      // Blur ONLY the PDP section
      const pdpSection = document.getElementById(`MainProduct-${this.sectionId}`)
        || this.container.closest('.main-product-template')
        || this.container.closest('section')
        || document.querySelector('.main-product-template')
        || document.querySelector('.main-product_info');
      if (pdpSection) {
        pdpSection.classList.add('luxury-pdp-blurred');
        this.pdpSection = pdpSection;
      }

      // Lock all page scrolling completely
      document.documentElement.classList.add('luxury-popup-locked');
      document.body.classList.add('luxury-popup-locked');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';

      // Pause Lenis smooth scrolling
      if (window.lenis && typeof window.lenis.stop === 'function') {
        window.lenis.stop();
      }

      // Prevent wheel and touch scrolling on page
      if (!this._scrollBlocker) {
        this._scrollBlocker = (e) => {
          if (this.popup && !this.popup.hidden) {
            e.preventDefault();
          }
        };
      }
      window.addEventListener('wheel', this._scrollBlocker, { passive: false });
      window.addEventListener('touchmove', this._scrollBlocker, { passive: false });

      this.popup.hidden = false;
      this.popup.removeAttribute('hidden');
      this.popup.style.display = 'flex';
      this.popup.classList.add('is-visible');

      const firstBtn = this.popup.querySelector('[data-choose-mode]');
      if (firstBtn) firstBtn.focus();
    }

    hideModeErrorPopup() {
      if (!this.popup) return;
      this.popup.hidden = true;
      this.popup.setAttribute('hidden', '');
      this.popup.style.display = 'none';
      this.popup.classList.remove('is-visible');

      // Remove blur from the PDP section
      if (this.pdpSection) {
        this.pdpSection.classList.remove('luxury-pdp-blurred');
      } else {
        const pdpSection = document.querySelector('.main-product-template')
          || document.querySelector('.main-product_info');
        if (pdpSection) pdpSection.classList.remove('luxury-pdp-blurred');
      }

      // Unlock all page scrolling
      document.documentElement.classList.remove('luxury-popup-locked');
      document.body.classList.remove('luxury-popup-locked');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';

      // Resume Lenis smooth scrolling
      if (window.lenis && typeof window.lenis.start === 'function') {
        window.lenis.start();
      }

      // Remove scroll event blockers
      if (this._scrollBlocker) {
        window.removeEventListener('wheel', this._scrollBlocker);
        window.removeEventListener('touchmove', this._scrollBlocker);
      }
    }

    resetSubmitButtonState() {
      if (!this.form) return;
      const submitBtn = this.form.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.classList.remove('loading');
        submitBtn.removeAttribute('aria-disabled');
      }
      const buyNowBtn = this.form.querySelector('[data-buy-it-now], .luxury-buy-it-now-btn');
      if (buyNowBtn) {
        buyNowBtn.classList.remove('loading');
        buyNowBtn.removeAttribute('aria-disabled');
      }
      const spinners = this.form.querySelectorAll('.loading-overlay__spinner');
      spinners.forEach((spinner) => spinner.classList.add('hidden'));
    }
  }

  // Initialize on DOM ready and section load
  function initProductCustomization() {
    document.querySelectorAll('.luxury-customization-system').forEach((el) => {
      if (!el._customizationController) {
        el._customizationController = new ProductCustomizationController(el);
      }
    });
  }

  // Expose globally for dynamic components (e.g. quick-add-modal)
  window.initProductCustomization = initProductCustomization;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductCustomization);
  } else {
    initProductCustomization();
  }

  // Support Shopify Theme Editor dynamic section reload
  document.addEventListener('shopify:section:load', initProductCustomization);

  // Global Buy It Now listener fallback
  document.addEventListener('click', function(e) {
    if (e.__buyNowHandled) return;
    const buyBtn = e.target.closest(
      '[data-buy-it-now], .luxury-buy-it-now-btn, .shopify-payment-button__button, .shopify-payment-button, [data-testid="Checkout-button"]'
    );
    if (!buyBtn) return;

    // Check if there is an active customization system on the page
    const productScope = buyBtn.closest('.product, .main-product-template, [id^="MainProduct-"], .product__info-container, quick-add-modal') || document;
    const customSystem = productScope.querySelector('.luxury-customization-system') || document.querySelector('.luxury-customization-system');

    if (customSystem) {
      const controller = customSystem._customizationController;
      if (controller && !controller.mode) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        controller.resetSubmitButtonState();
        controller.showModeErrorPopup();
        return false;
      } else if (!controller) {
        const popup = document.querySelector('.luxury-mode-popup-overlay');
        if (popup) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          if (popup.parentElement !== document.body) document.body.appendChild(popup);
          popup.hidden = false;
          popup.removeAttribute('hidden');
          popup.style.display = 'flex';
          popup.classList.add('is-visible');
          return false;
        }
      }
    }

    const form = buyBtn.closest('form');
    if (!form) return;

    e.preventDefault();
    e.stopPropagation();

    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.disabled = false;
    }

    buyBtn.classList.add('loading');
    buyBtn.setAttribute('aria-disabled', 'true');
    const spinner = buyBtn.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.remove('hidden');

    const checkoutUrl = (window.routes && window.routes.checkout_url) || '/checkout';
    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

    const formData = new FormData(form);

    fetch(addUrl, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript, application/json'
      },
      body: formData
    })
    .then((res) => {
      if (!res.ok) return res.json().then((errData) => Promise.reject(errData));
      return res.json();
    })
    .then(() => {
      window.location.href = checkoutUrl;
    })
    .catch((err) => {
      console.warn('Fallback Buy It Now redirecting to checkout:', err);
      window.location.href = checkoutUrl;
    });
  }, false);
})();
